<!--
  Merge Quotations — Moderator / Administrator only.

  Merges several Approved quotations of the same client into one "main"
  quotation. The main quotation keeps its own annex data (top/bottom mail,
  incoterms, addresses, tag, dates...); the product lines of the other
  quotations are copied into it — and their internal notes (mail_top) and client
  references (ref_client) appended to its own — then those quotations are set
  back to Pending: they stay in the list WITH their lines, as a record, with an
  audit entry saying which quotation they were merged with. Being Pending is what
  keeps them out of the projected stock and the approved backlog.

  Addresses do not restrict anything: quotations of the same client are listed
  whatever their billing/delivery address, a difference is only flagged.

  Only the API decides what is mergeable: this popup lists the candidates the
  API returns, then asks it for a dry-run preview before letting the user
  confirm. The same rules are re-checked on execute.
-->
<template>
  <vs-popup fullscreen title="Merge Quotations" :active.sync="isActive">
    <div class="merge-quotations">

      <vs-alert color="primary" icon-pack="feather" icon="icon-info" active="true" class="mb-4">
        Every <b>Approved</b> quotation of the same <b>client</b> without a final invoice can be merged —
        whatever the status shown in the list (Advance paid, Awaiting transport, Transport assigned...)
        and with or without a transport. An advance invoice is allowed; a plain or final invoice is not.
        A different <b>billing</b> or <b>delivery address</b> does not block the merge, it is only flagged.
        Pick the <b>main</b> quotation: it keeps its own data (top/bottom mail, incoterms, addresses, tag,
        dates) and receives all the products. The <b>internal notes</b> and the <b>client references</b>
        are the exception: they are concatenated, not replaced. A <b>service</b> (Transport, Groupage,
        Unpacking fee...) with different prices is not a conflict: its amounts are <b>added</b> into one
        line. The other quotations go back to
        <b>Pending</b>: they stay in the list
        with their lines (as a record), with an audit entry saying what they were merged with.
      </vs-alert>

      <!-- ── 1. Pick the quotations ─────────────────────────────────────── -->
      <div class="flex flex-wrap items-center mb-4">
        <h5 class="flex-grow mb-0">1. Select the quotations</h5>
        <vs-input class="mr-2" icon-pack="feather" icon="icon-search" icon-no-border
          v-model="search" placeholder="Search client..." />
        <vs-button color="primary" type="border" size="small" @click="loadCandidates" :disabled="loadingCandidates">
          Refresh
        </vs-button>
      </div>

      <div v-if="loadingCandidates" class="mb-4">Loading mergeable quotations...</div>

      <vs-alert v-if="!loadingCandidates && !groups.length" color="warning" active="true" class="mb-4">
        No mergeable quotations found. A merge needs at least 2 Approved quotations of the
        same client without a plain or final invoice.
      </vs-alert>

      <div v-for="group in filteredGroups" :key="group.key" class="mb-4">
        <div class="merge-group p-4 rounded-lg"
          :class="{ 'merge-group--disabled': selectedGroupKey && selectedGroupKey !== group.key }">
          <div class="mb-2">
            <span class="font-semibold text-lg">{{ group.client }}</span>
            <span class="ml-2 text-sm opacity-75">({{ group.quotations.length }} quotations)</span>
          </div>

          <!-- Addresses never block a merge, but the merged document only keeps
               the main quotation's ones: say it before anything is picked. -->
          <vs-alert v-if="hasMixedAddresses(group)" color="warning" icon-pack="feather"
            icon="icon-map-pin" active="true" class="mb-3">
            These quotations do not all have the same billing or delivery address.
            Merging is allowed: the merged quotation keeps the <b>main</b> one's addresses.
          </vs-alert>

          <table class="merge-table w-full">
            <thead>
              <tr>
                <th style="width:60px">Merge</th>
                <th style="width:60px">Main</th>
                <th>Ref</th>
                <th>Date</th>
                <th>Expected</th>
                <th>Tag</th>
                <th class="text-right">Lines</th>
                <th class="text-right">Vol. (m3)</th>
                <th class="text-right">Price (EUR)</th>
                <th>Billing</th>
                <th>Delivery</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="q in group.quotations" :key="q.id">
                <td>
                  <vs-checkbox :value="isSelected(q.id)" @input="toggle(group, q.id, $event)" />
                </td>
                <td>
                  <vs-radio v-if="isSelected(q.id)" v-model="mainId" :vs-value="q.id" :vs-name="'main-' + group.key" />
                  <span v-else>-</span>
                </td>
                <td class="font-semibold">#{{ q.id }}</td>
                <td>{{ q.date }}</td>
                <td>{{ q.expected || '-' }}</td>
                <td>{{ q.tag || '-' }}</td>
                <td class="text-right">{{ q.nblines }}</td>
                <td class="text-right">{{ q.volume.toFixed(1) }}</td>
                <td class="text-right">{{ money(q.total) }}</td>
                <td class="merge-address" :class="{ 'merge-address--differs': differsFromMain(group, q, 'idbilling') }"
                  :title="q.billing_label">
                  {{ q.billing_label }}
                </td>
                <td class="merge-address" :class="{ 'merge-address--differs': differsFromMain(group, q, 'iddelivery') }"
                  :title="q.delivery_label">
                  {{ q.delivery_label }}
                </td>
                <td>
                  <vs-chip v-if="q.has_advance" color="warning" class="mr-1">Advance invoice</vs-chip>
                  <vs-chip v-if="q.has_transport" color="warning" class="mr-1">Transport</vs-chip>
                  <vs-chip v-if="q.is_parent" color="warning" class="mr-1">Parent</vs-chip>
                  <vs-chip v-if="q.idparent" color="warning" class="mr-1">Child of #{{ q.idparent }}</vs-chip>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── 2. Check ───────────────────────────────────────────────────── -->
      <div class="flex flex-wrap items-center mt-6 mb-4">
        <h5 class="flex-grow mb-0">2. Check the merge</h5>
        <vs-button color="dark" type="border" size="small" class="mr-2" v-if="selectedIds.length"
          @click="clearSelection">
          Clear selection
        </vs-button>
        <vs-button color="primary" :disabled="selectedIds.length < 2 || loadingPreview" @click="runPreview">
          {{ loadingPreview ? 'Checking...' : 'Check ' + selectedIds.length + ' quotations' }}
        </vs-button>
      </div>

      <vs-alert v-if="selectedIds.length && selectedIds.length < 2" color="warning" active="true" class="mb-4">
        Select at least 2 quotations.
      </vs-alert>

      <div v-if="preview">
        <vs-alert v-for="(e, i) in preview.errors" :key="'e' + i" color="danger" icon-pack="feather"
          icon="icon-x-circle" active="true" class="mb-2">
          {{ e.message }}
        </vs-alert>

        <!-- Same product, different price: this is what blocks the merge -->
        <div v-if="preview.conflicts.length" class="mb-4">
          <vs-alert color="danger" icon-pack="feather" icon="icon-alert-triangle" active="true" class="mb-2">
            <b>Price conflict.</b> The same product has a different price in the selected quotations.
            Merging would silently change a price, so it is blocked. Fix the prices in the quotations
            (or unselect one), then check again. Services (Transport, Unpacking fee...) never conflict
            on their price — their amounts are added — but a different VAT rate still blocks them.
            <div class="mt-1">
              This quotation is billed in <b>{{ preview.currency }}</b> (billing address country),
              so only the {{ preview.currency }} price is compared — a difference on the other
              currency is just the exchange rate and is listed as a warning.
            </div>
          </vs-alert>
          <table class="merge-table w-full">
            <thead>
              <tr>
                <th>Product</th>
                <th>What differs</th>
                <th class="text-right">Reference</th>
                <th class="text-right">Does not match</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in preview.conflicts" :key="'c' + i">
                <td>{{ c.product }}</td>
                <td>{{ c.field }}</td>
                <td class="text-right">#{{ c.reference_quotation }} → {{ c.reference_value }}</td>
                <td class="text-right text-danger font-semibold">#{{ c.other_quotation }} → {{ c.other_value }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <vs-alert v-for="(w, i) in preview.warnings" :key="'w' + i" color="warning" icon-pack="feather"
          icon="icon-alert-circle" active="true" class="mb-2">
          #{{ w.id }} — {{ w.message }}
        </vs-alert>

        <!-- Result preview -->
        <div v-if="preview.plan.length" class="mt-4">
          <h6 class="mb-2">
            Result on quotation #{{ mainId }} — {{ preview.plan.length }} product lines,
            {{ preview.totals.volume.toFixed(1) }} m3, {{ money(documentTotal) }} {{ preview.currency }}
          </h6>
          <table class="merge-table w-full">
            <thead>
              <tr>
                <th>Product</th>
                <th class="text-right">Unit price ({{ preview.currency }})</th>
                <th v-for="id in selectedIds" :key="'h' + id" class="text-right">
                  #{{ id }}<span v-if="id === mainId"> (main)</span>
                </th>
                <th class="text-right">Merged qty</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in preview.plan" :key="line.idproduct">
                <td>{{ line.product }}</td>
                <td class="text-right">{{ money(unitPrice(line)) }}</td>
                <td v-for="id in selectedIds" :key="line.idproduct + '-' + id" class="text-right">
                  {{ qtyFor(line, id) }}
                </td>
                <td class="text-right font-semibold">{{ absQty(line.merged_quantity) }}</td>
                <td>
                  <vs-chip v-if="line.prices_summed" color="warning">Service: amounts added</vs-chip>
                  <vs-chip v-else-if="line.action === 'add'" color="primary">Quantities added</vs-chip>
                  <vs-chip v-else-if="line.action === 'copy'" color="success">Copied to #{{ mainId }}</vs-chip>
                  <vs-chip v-else color="dark">Unchanged</vs-chip>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── 3. Merge ───────────────────────────────────────────────────── -->
      <div class="flex flex-wrap items-center mt-6 mb-2">
        <h5 class="flex-grow mb-0">3. Merge</h5>
        <vs-button color="success" :disabled="!canMerge || merging" @click="confirmMerge">
          {{ merging ? 'Merging...' : mergeButtonLabel }}
        </vs-button>
      </div>
      <p v-if="preview && preview.ok" class="text-sm opacity-75 mb-4">
        Quotation #{{ mainId }} keeps its data, receives all the products and gets the other
        internal notes and client references appended to its own.
        Quotation<span v-if="otherIds.length > 1">s</span>
        #{{ otherIds.join(', #') }} keep their lines and are set back to <b>Pending</b>.
      </p>
    </div>
  </vs-popup>
</template>

<script>
import axios from '@/axios.js'

export default {
  name: 'MergeQuotations',
  props: {
    active: { type: Boolean, default: false }
  },
  data () {
    return {
      search: '',
      groups: [],
      loadingCandidates: false,
      selectedIds: [],
      selectedGroupKey: null,
      mainId: null,
      preview: null,
      loadingPreview: false,
      merging: false
    }
  },
  computed: {
    isActive: {
      get () { return this.active },
      set (val) { this.$emit('update:active', val) }
    },
    filteredGroups () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.groups
      return this.groups.filter(g => (g.client || '').toLowerCase().includes(q))
    },
    otherIds () {
      return this.selectedIds.filter(id => id !== this.mainId)
    },
    canMerge () {
      return !!(this.preview && this.preview.ok && this.selectedIds.length > 1 && this.mainId)
    },
    // Both prices are stored on every line; the one that matters is the one the
    // document is billed in, which the API resolves from the billing country.
    documentTotal () {
      if (!this.preview) return 0
      return this.preview.currency === 'RON' ? this.preview.totals.total_ron : this.preview.totals.total
    },
    mergeButtonLabel () {
      if (!this.canMerge) return 'Merge'
      return `Merge into #${this.mainId}`
    }
  },
  watch: {
    active (val) {
      if (val) {
        this.clearSelection()
        this.loadCandidates()
      }
    },
    // Changing the main quotation changes which prices are the reference, so the
    // previous check is no longer the one the user is looking at.
    mainId () { this.preview = null }
  },
  methods: {
    money (value) {
      return (Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    absQty (value) {
      return Math.abs(Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 3 })
    },
    unitPrice (line) {
      return this.preview && this.preview.currency === 'RON' ? line.unit_price_ron : line.unit_price
    },
    // A summed service has no common unit price: show what each quotation
    // brings ("1 × 500.00") so the merged price can be checked by eye.
    qtyFor (line, id) {
      const found = line.per_quotation.find(q => q.id === id)
      if (!found) return '-'
      if (!line.prices_summed) return this.absQty(found.quantity)
      return `${this.absQty(found.quantity)} × ${this.money(this.unitPrice(found))}`
    },
    isSelected (id) {
      return this.selectedIds.indexOf(id) !== -1
    },
    // Two quotations of the same client can carry different addresses, and the
    // merged document keeps the main one's: both the group banner and the cell
    // marker exist so nobody discovers it after the merge.
    hasMixedAddresses (group) {
      const first = group.quotations[0]
      if (!first) return false
      return group.quotations.some(q => q.idbilling !== first.idbilling || q.iddelivery !== first.iddelivery)
    },
    differsFromMain (group, quotation, field) {
      if (!this.mainId || this.selectedGroupKey !== group.key) return false
      const main = group.quotations.find(q => q.id === this.mainId)
      return !!main && main[field] !== quotation[field]
    },
    clearSelection () {
      this.selectedIds = []
      this.selectedGroupKey = null
      this.mainId = null
      this.preview = null
    },
    // A merge only makes sense inside one group (same client + addresses), so the
    // first pick locks the group; unselecting everything unlocks it.
    toggle (group, id, checked) {
      if (checked) {
        if (this.selectedGroupKey && this.selectedGroupKey !== group.key) return
        this.selectedGroupKey = group.key
        if (!this.isSelected(id)) this.selectedIds.push(id)
        if (!this.mainId) this.mainId = id
      } else {
        this.selectedIds = this.selectedIds.filter(x => x !== id)
        if (this.mainId === id) this.mainId = this.selectedIds.length ? this.selectedIds[0] : null
        if (!this.selectedIds.length) this.selectedGroupKey = null
      }
      this.selectedIds.sort((a, b) => a - b)
      this.preview = null
    },
    loadCandidates () {
      this.loadingCandidates = true
      axios.get('/quotation/merge/candidates')
        .then(response => {
          this.groups = (response.data && response.data.groups) || []
          this.loadingCandidates = false
        })
        .catch(error => {
          this.loadingCandidates = false
          this.notifyError('Could not load mergeable quotations', error)
        })
    },
    runPreview () {
      this.loadingPreview = true
      this.preview = null
      axios.post('/quotation/merge/preview', { ids: this.selectedIds, mainId: this.mainId })
        .then(response => {
          this.preview = response.data
          this.loadingPreview = false
        })
        .catch(error => {
          this.loadingPreview = false
          this.notifyError('Check failed', error)
        })
    },
    confirmMerge () {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm merge',
        text: `Merge ${this.selectedIds.length} quotations into #${this.mainId}? `
          + `Quotation${this.otherIds.length > 1 ? 's' : ''} #${this.otherIds.join(', #')} `
          + 'will keep their lines and be set back to Pending. This cannot be undone from the interface.',
        accept: this.doMerge,
        acceptText: 'Merge',
        cancelText: 'Cancel'
      })
    },
    doMerge () {
      this.merging = true
      axios.post('/quotation/merge/execute', {
        ids: this.selectedIds,
        mainId: this.mainId,
        who: localStorage.getItem('login-username')
      })
        .then(response => {
          this.merging = false
          this.$vs.notify({
            title: 'Merge done',
            text: (response.data && response.data.message) || 'Quotations merged.',
            color: 'success',
            iconPack: 'feather',
            icon: 'icon-check',
            position: 'bottom-right',
            time: 6000
          })
          this.$emit('merged', response.data)
          this.isActive = false
        })
        .catch(error => {
          this.merging = false
          // The API re-checks every rule: show what it refused and refresh the
          // check so the popup reflects the real state.
          const data = error && error.response && error.response.data
          if (data && (data.errors || data.conflicts)) {
            this.preview = {
              ok: false,
              errors: data.errors || [],
              warnings: [],
              conflicts: data.conflicts || [],
              plan: [],
              currency: (this.preview && this.preview.currency) || 'EUR',
              totals: { volume: 0, total: 0, total_ron: 0 }
            }
          }
          this.notifyError('Merge refused', error)
        })
    },
    notifyError (title, error) {
      const data = error && error.response && error.response.data
      const detail = (data && (data.message
        || (data.errors && data.errors.length && data.errors[0].message))) || (error && error.message) || ''
      this.$vs.notify({
        title,
        text: detail,
        color: 'danger',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        position: 'bottom-right',
        time: 8000
      })
    }
  }
}
</script>

<style lang="scss">
.merge-quotations {
  .merge-group {
    border: 1px solid rgba(0, 0, 0, 0.12);
  }

  .merge-group--disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  .merge-table {
    border-collapse: collapse;
    font-size: 0.9rem;

    th,
    td {
      padding: 0.4rem 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      vertical-align: middle;
      white-space: nowrap;
    }

    th {
      text-align: left;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.72rem;
      opacity: 0.7;
    }
  }

  // Addresses are long: keep the row readable, the full value is in the title.
  .merge-address {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .merge-address--differs {
    color: rgba(var(--vs-warning), 1);
    font-weight: 600;
  }
}
</style>
